import { DeliveryOrPickupText, DoordashHighlight, NotAvailableText, TabItem, Tabs, Wrapper } from './OrderType.styled';
import { faMotorcycle, faShoppingBag } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';

import { AddressesWrapper } from '../../Addresses/AddressesWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { OrderDeliveryType } from '../../../types/orders';
import { ScheduleOrderWrapper } from '../Schedule';
import type { state } from '../../../types/store/state';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { toast } from 'react-toastify';

interface Props {
  setOrderType: (type: OrderDeliveryType) => void;
  programmedTime: string;
  setProgrammedTime: (time: string) => void;
}

export const OrderType = (props: Props) => {
  const { t } = useTranslation();
  const { hasDelivery, hasPickup, address } = useSelector(({ branchStore }: state) => branchStore);
  const [selectedOrderType, setSelectedOrderType] = useState<OrderDeliveryType>(
    hasPickup ? 'pickup' : hasDelivery ? 'delivery' : 'pickup'
  );
  const deliveryOrPickupText = selectedOrderType === 'pickup'
    ? `${t('orders.pickup.tag.description')} ${address.address} ${address.city} ${address.state}`
    : (
      <DeliveryOrPickupText>
        {t('orders.delivery.tag.description').split('Doordash')[0]}
        <DoordashHighlight>Doordash</DoordashHighlight>
        {t('orders.delivery.tag.description').split('Doordash')[1]}
      </DeliveryOrPickupText>
    );

  useEffect(() => {
    toast.clearWaitingQueue();

    if (hasPickup || hasDelivery) {
      props.setOrderType(selectedOrderType);

      if (selectedOrderType === 'delivery') {
        toast.info(
          <span>
            {t('orders.delivery.tag.toastify.title').split('Doordash')[0]}
            <DoordashHighlight variant='toast'>Doordash</DoordashHighlight>
          </span>
        );
      } else if (selectedOrderType === 'pickup') {
        toast.info(t('orders.pickup.tag.toastify.title'));
      }
    }
  }, [selectedOrderType, hasPickup, hasDelivery]);

  const getContent = () => (
    <>
      <DeliveryOrPickupText>{deliveryOrPickupText}</DeliveryOrPickupText>
      <ScheduleOrderWrapper
        programmedTime={props.programmedTime}
        setProgrammedTime={props.setProgrammedTime}
      />
      {selectedOrderType !== 'pickup' &&
        <AddressesWrapper condensed={true} />
      }
    </>
  );

  return (
    <Wrapper>
      <Tabs>
        <TabItem
          onClick={() => hasPickup && setSelectedOrderType('pickup')}
          active={selectedOrderType === 'pickup'}
          disabled={!hasPickup}
        >
          <FontAwesomeIcon icon={faShoppingBag} width={16} className="faIcon" />
          {t('orders.pickup.tag.title')}
          {!hasPickup && <NotAvailableText>
            {t('orders.notAvailable.tag.title')}
          </NotAvailableText>}
        </TabItem>
        <TabItem
          onClick={() => hasDelivery && setSelectedOrderType('delivery')}
          active={selectedOrderType === 'delivery'}
          disabled={!hasDelivery}
        >
          <FontAwesomeIcon icon={faMotorcycle} width={16} className="faIcon" />
          {t('orders.delivery.tag.title')}
          {!hasDelivery && <NotAvailableText>
            {t('orders.notAvailable.tag.title')}
          </NotAvailableText>}
        </TabItem>
      </Tabs>
      {getContent()}
    </Wrapper>
  );
};